import React, { useEffect, useState, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, Textarea } from '../form/input';
import styles from './styles.module.scss';
import { AddIcon, MagicPen, RemoveIcon, UploadIconThree } from 'assets';
import { useOpenEndedQuestionPost, OpenEndedQuestionPayload, useClickOutside } from 'hooks';
import { useToast } from 'hooks/useToast';
import { useRefineText } from 'hooks/useRefineText'; // New hook
import RefineModal from 'components/modal-variants/refine-modal';

interface QuestionData {
  id: string;
  question: string;
  description?: string;
  image?: string;
  required: boolean;
  wordLimit: boolean;
  wordLimitValue?: number;
  pointsObtainable: number;
}

interface QuestionFormData {
  questions: QuestionData[];
}

interface QuestionFormProps {
  assessmentId: string | null;
  submitQuestion: (options?: { skipped?: boolean }) => void;
  setImageUploadScroll: (boolean) => void;
  questionsData: any;
  isSubmitted: boolean;
  creditsAvailable?: number;
  onInsufficientCredits?: (creditsRequired: number, creditsAvailable: number) => void;
}

const questionSchema = yup.object().shape({
  id: yup.string().required(),
  question: yup.string().required('Question is required'),
  description: yup.string().optional(),
  image: yup.string().optional(),
  required: yup.boolean().default(false),
  wordLimit: yup.boolean().default(false),
  wordLimitValue: yup.number().when('wordLimit', {
    is: true,
    then: (schema) => schema
      .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
      .typeError('Please enter a valid number for word limit')
      .required('Word limit is required when word limit is enabled')
      .min(1, 'Word limit must be at least 1'),
    otherwise: (schema) => schema.optional(),
  }),
  pointsObtainable: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .typeError('Please enter a valid number for points')
    .min(1, 'Points must be at least 1')
    .required('Points obtainable is required'),
});

const questionFormSchema = yup.object().shape({
  questions: yup
    .array()
    .of(questionSchema)
    .min(1, 'At least one question is required')
    .max(3, 'Maximum 3 questions allowed')
    .required('Questions array is required'),
});

const normalizeQuestionData = (questionData: any, index: number): QuestionData => ({
  id: (index + 1).toString(),
  question: questionData?.question_content || questionData?.question || '',
  description: questionData?.description || '',
  image: questionData?.image || '',
  required: questionData?.required || false,
  wordLimit: (questionData?.word_limit ?? questionData?.wordLimitValue ?? 0) > 32,
  wordLimitValue: (questionData?.word_limit ?? questionData?.wordLimitValue ?? 0) > 32
    ? (questionData?.word_limit ?? questionData?.wordLimitValue)
    : undefined,
  pointsObtainable: questionData?.points_obtainable || questionData?.pointsObtainable || 0,
});

const transformQuestionsData = (questionsData: any): QuestionData[] => {
  if (!questionsData) {
    return [{ id: '1', question: '', description: '', image: '', required: false, wordLimit: false, wordLimitValue: undefined, pointsObtainable: 0 }];
  }

  if (Array.isArray(questionsData)) {
    const mappedQuestions = questionsData
      .slice(0, 3)
      .map((questionData, index) => normalizeQuestionData(questionData, index));
    return mappedQuestions.length > 0 ? mappedQuestions : [{ id: '1', question: '', description: '', image: '', required: false, wordLimit: true, wordLimitValue: 100, pointsObtainable: 5 }];
  }

  const sourceQuestions = Array.isArray(questionsData.questions)
    ? questionsData.questions
    : questionsData.questions && typeof questionsData.questions === 'object'
      ? Object.values(questionsData.questions)
      : questionsData.question1 || questionsData.question2 || questionsData.question3
        ? ['question1', 'question2', 'question3'].map((key) => questionsData[key]).filter(Boolean)
        : [];

  if (sourceQuestions.length > 0) {
    const mappedQuestions = sourceQuestions.slice(0, 3).map((questionData, index) => normalizeQuestionData(questionData, index));
    return mappedQuestions.length > 0 ? mappedQuestions : [{ id: '1', question: '', description: '', image: '', required: false, wordLimit: true, wordLimitValue: 100, pointsObtainable: 5 }];
  }

  const questions: QuestionData[] = [];
  const questionKeys = ['question1', 'question2', 'question3'];
  questionKeys.forEach((key, index) => {
    const questionData = questionsData[key];
    if (questionData) {
      questions.push(normalizeQuestionData(questionData, index));
    }
  });
  if (questions.length === 0) {
    return [{ id: '1', question: '', description: '', image: '', required: false, wordLimit: true, wordLimitValue: 100, pointsObtainable: 5 }];
  }
  return questions;
};

const QuestionForm: React.FC<QuestionFormProps> = ({ assessmentId, submitQuestion, setImageUploadScroll, questionsData, isSubmitted, creditsAvailable = 0, onInsufficientCredits }) => {
  const { openToast } = useToast();
  const { mutate: submitOpenEndedQuestion, isPending } = useOpenEndedQuestionPost();
  const modalRef = useRef(null);
  const [uploadedImages, setUploadedImages] = useState<{ [key: string]: { base64: string; fileName: string } }>({});
  const [previousImageCount, setPreviousImageCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [refinedText, setRefinedText] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const textareaRef = useRef<any>(null);
  const { refineText, isLoading } = useRefineText({
    onSuccess: (refinedText) => {
      setRefinedText(refinedText);
      setModalText(refinedText);
      setShowPromptInput(true);
    },
  });
  useClickOutside(modalRef, () => setModalOpen(false));
  const defaultQuestions = transformQuestionsData(questionsData);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<QuestionFormData>({
    resolver: yupResolver(questionFormSchema),
    defaultValues: { questions: defaultQuestions },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'questions',
  });

  useEffect(() => {
    const newDefaultQuestions = transformQuestionsData(questionsData);
    reset({ questions: newDefaultQuestions });
    replace(newDefaultQuestions);
    const newUploadedImages: { [key: string]: { base64: string; fileName: string } } = {};
    newDefaultQuestions.forEach((question) => {
      if (question.image) {
        newUploadedImages[question.id] = { base64: question.image, fileName: 'Uploaded Image' };
      }
    });
    setUploadedImages(newUploadedImages);
  }, [questionsData, replace, reset]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, questionId: string) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setUploadedImages((prev) => ({ ...prev, [questionId]: { base64, fileName: file.name } }));
        const questionIndex = fields.findIndex((field) => field.id === questionId);
        if (questionIndex !== -1) {
          setValue(`questions.${questionIndex}.image`, base64);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const addQuestion = () => {
    if (fields.length < 4) {
      append({
        id: Date.now().toString(),
        question: '',
        description: '',
        image: '',
        required: false,
        wordLimit: false,
        wordLimitValue: undefined,
        pointsObtainable: 0,
      });
    }
  };

  const removeQuestion = (index: number) => {
    if (fields.length > 1) {
      const questionId = fields[index].id;
      remove(index);
      setUploadedImages((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  const onSubmit = async (data: QuestionFormData) => {
    if (!assessmentId) {
      openToast({ show: true, heading: 'Required', text: 'Assessment ID is required', type: 'error', timeOut: 5000 });
      return;
    }
    const payload: OpenEndedQuestionPayload = { assessment_id: assessmentId, question1: null, question2: null, question3: null };
    data.questions.slice(0, 3).forEach((question, index) => {
      payload[`question${index + 1}`] = {
        question_content: question.question,
        image: question.image || '',
        description: question.description || '',
        question_type: 'open_ended',
        points_obtainable: question.pointsObtainable,
        required: question.required,
        word_limit: question.wordLimit ? question.wordLimitValue || 32 : 32,
      };
    });
    submitOpenEndedQuestion(payload, {
      onSuccess: () => {
        submitQuestion();
        openToast({ show: true, heading: 'Success', text: 'Open-ended question(s) created successfully!', type: 'info', timeOut: 5000 });
      },
      onError: (err: any) => {
        console.error('Unexpected error during submission:', err);
        openToast({ show: true, heading: 'Submission Failed', text: err?.response?.data?.message || 'An unexpected error occurred. Please try again.', type: 'error', timeOut: 5000 });
      },
    });
  };

  const handleMagicPenClick = (index: number, question: string) => {
    setModalText(question);
    setModalOpen(true);
  };

  const handleRefine = (prompt: string) => {
    if (!modalText) return;
    const creditsNeeded = 3;
    if (creditsAvailable < creditsNeeded) {
      onInsufficientCredits?.(creditsNeeded, creditsAvailable);
      return;
    }
    refineText({
      question: modalText,
      prompt: prompt,
    });
  };

  const handleClose = () => {
    setModalOpen(false);
    setRefinedText('');
    setShowPromptInput(false);
    setModalText(watch(`questions.${questionIndex}.question`))
  };

  const handleInsert = (index: number) => {
    const questionIndex = fields.findIndex((field) => field.id === fields[index].id);
    if (questionIndex !== -1) {
      setValue(`questions.${questionIndex}.question`, refinedText);
      if (textareaRef.current) {
        textareaRef.current.value = refinedText;
      }
    }
    setModalOpen(false);
    setRefinedText('');
    setQuestionIndex(index)
  };

  useEffect(() => {
    const currentImageCount = Object.keys(uploadedImages).length;
    if (currentImageCount > previousImageCount) {
      setImageUploadScroll(true);
    }
    setPreviousImageCount(currentImageCount);
  }, [uploadedImages, previousImageCount]);

  return (
    <div className={styles.questionForm}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <div className={styles.questionsSection}>
          {fields.map((field, index) => {
            const watchRequired = watch(`questions.${index}.required`);
            const watchWordLimit = watch(`questions.${index}.wordLimit`);
            return (
              <div key={field.id} className={styles.questionItem}>
                {(uploadedImages[field.id] || watch(`questions.${index}.image`)) && (
                  <div className={styles.imagePreview}>
                    <img src={uploadedImages[field.id]?.base64 || watch(`questions.${index}.image`)} alt="Preview" className={styles.previewImage} />
                  </div>
                )}
                <div className={styles.questionHeader}>
                  {fields.length > 1 && <span className={styles.questionNumber}>Question {index + 1}</span>}
                  {fields.length > 1 && <button type="button" onClick={() => removeQuestion(index)} className={styles.removeButton}>✕</button>}
                </div>
                <div className={styles.questionSection}>
                  <div className={styles.questionInputContainer}>
                    <Textarea
                      name={`questions.${index}.question`}
                      placeholder="Type your question here"
                      register={register}
                      value={watch(`questions.${index}.question`) || ''}
                      validatorMessage={errors.questions?.[index]?.question?.message}
                      className={styles.questionInput}
                      parentClassName={styles.questionInputWrapper}
                      required
                    />
                    <MagicPen className={styles.magicPen} onClick={() => handleMagicPenClick(index, watch(`questions.${index}.question`))} />
                  </div>
                </div>
                <div className={styles.descriptionSection}>
                  <Input
                    name={`questions.${index}.description`}
                    placeholder="Description (Optional)"
                    register={register}
                    value={watch(`questions.${index}.description`) || ''}
                    validatorMessage={errors.questions?.[index]?.description?.message}
                    className={styles.descriptionInput}
                  />
                </div>
                <div className={styles.controlsSection}>
                  <div className={styles.controls}>
                    {(uploadedImages[field.id] || watch(`questions.${index}.image`)) ? (
                      <div className={styles.imageUpload}>
                        <div className={styles.fileNameContainer}>
                          <span className={styles.fileName}>{uploadedImages[field.id]?.fileName || 'Uploaded Image'}</span>
                          <span
                            onClick={() => {
                              setUploadedImages((prev) => {
                                const updated = { ...prev };
                                delete updated[field.id];
                                return updated;
                              });
                              setValue(`questions.${index}.image`, '');
                            }}
                            className={styles.removeImageButton}
                          >
                            <RemoveIcon className={styles.removeIcon} />
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.imageUpload}>
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, field.id)} className={styles.fileInput} id={`image-${field.id}`} />
                        <label htmlFor={`image-${field.id}`} className={styles.uploadButton}>
                          <UploadIconThree className={styles.uploadIcon} /> Upload image
                        </label>
                      </div>
                    )}
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleLabel}>
                        Required
                        <input type="checkbox" {...register(`questions.${index}.required`)} className={styles.toggleInput} />
                        <span className={`${styles.toggleSlider} ${watchRequired ? styles.active : ''}`}>
                          <span className={styles.toggleButton}></span>
                        </span>
                      </label>
                    </div>
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleLabel}>
                        Word limit
                        <input type="checkbox" {...register(`questions.${index}.wordLimit`)} className={styles.toggleInput} />
                        <span className={`${styles.toggleSlider} ${watchWordLimit ? styles.active : ''}`}>
                          <span className={styles.toggleButton}></span>
                        </span>
                      </label>
                      {watchWordLimit && (
                        <Input
                          name={`questions.${index}.wordLimitValue`}
                          type="number"
                          placeholder="0000"
                          register={register}
                          value={watch(`questions.${index}.wordLimitValue`)?.toString() || ''}
                          validatorMessage={(errors.questions?.[index]?.wordLimitValue?.message as string) ?? ''}
                          className={styles.numberInput}
                          parentClassName={styles.numberInputContainer}
                          min={1}
                        />
                      )}
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={styles.inputLabel}>Points Obtainable</label>
                      <Input
                        name={`questions.${index}.pointsObtainable`}
                        type="number"
                        placeholder="8"
                        register={register}
                        value={watch(`questions.${index}.pointsObtainable`)?.toString() || ''}
                        validatorMessage={errors.questions?.[index]?.pointsObtainable?.message}
                        className={styles.numberInput}
                        parentClassName={styles.numberInputContainer}
                        min={1}
                        required
                      />
                    </div>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
        {!isSubmitted && (
          <div className={styles.actionButtons}>
            <button type="button" onClick={addQuestion} className={styles.addButton} disabled={fields.length >= 3}>
              <AddIcon className={styles.addIcon} /> Add
            </button>
            <button type="button" onClick={() => submitQuestion({ skipped: true })} className={styles.skipButton} disabled={isPending}>
              Skip
            </button>
            <button type="submit" className={styles.submitButton} disabled={isPending}>
              {isPending ? 'Submitting...' : 'Done'}
            </button>
          </div>
        )}
      </form>

      <RefineModal
        isOpen={modalOpen}
        onClose={handleClose}
        modalText={modalText}
        refinedText={refinedText}
        onRefine={handleRefine}
        onInsert={handleInsert}
        isLoading={isLoading}
        showInput={showPromptInput}
      />
    </div>
  );
};

export { QuestionForm };